import { Injectable, PLATFORM_ID, afterNextRender, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';

/**
 * Google Analytics (GA4, via Firebase Analytics). Dev and prod are backed by
 * separate Firebase Web Apps — each has its own measurementId (see
 * environment.ts / environment.prod.ts) — so traffic never mixes between the
 * two data streams.
 *
 * Loaded lazily and only in the browser: Analytics touches `window` and
 * IndexedDB, which don't exist during SSR/prerendering, and dynamically
 * importing `firebase/*` keeps it out of the server bundle and off the
 * critical path for first paint.
 */
@Injectable({ providedIn: 'root' })
export class Analytics {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private analytics: import('firebase/analytics').Analytics | undefined;

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      this.init().catch(() => {
        /* analytics unsupported/blocked (ad blocker, privacy mode, etc.) — fail silently */
      });
    });
  }

  private async init() {
    const [{ initializeApp }, { getAnalytics, isSupported, logEvent }] = await Promise.all([
      import('firebase/app'),
      import('firebase/analytics'),
    ]);

    if (!(await isSupported())) return;

    const app = initializeApp(environment.firebase);
    this.analytics = getAnalytics(app);

    const logPageView = (path: string) => {
      if (!this.analytics) return;
      logEvent(this.analytics, 'page_view', {
        page_path: path,
        page_title: document.title,
        page_location: window.location.href,
      });
    };

    // Initial load's page_view fires automatically on init; this only
    // covers subsequent client-side route changes.
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => logPageView(e.urlAfterRedirects));
  }

  /** Fire a custom GA4 event, e.g. trackEvent('enquiry_submitted', { tour_id }). */
  async trackEvent(name: string, params?: Record<string, unknown>) {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.analytics) return;
    const { logEvent } = await import('firebase/analytics');
    logEvent(this.analytics, name, params);
  }
}
